import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoginAuthUserInputFields } from '@com-delta-idp/mnx-login-ui';
import { OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import { CookieHelperService } from '@com-delta-idp/mnx-utils';
import { CoreUtilService } from '../../utils/core-util.service';
import { LoginHelperUtils } from '../../utils/login-helper.utils';
import {
  FROM_APP,
  RESTRICT_AUTH_CALLS_URL,
  VIEW_NAME
} from '../constants/login-constant';
import { LoginModalContent } from '../models/aem';
import { LoginModalHelperService, NextDisplayViewService } from '../services';
import { PingLoginAdapterService } from './ping-login-adapter.service';

@Injectable({
  providedIn: 'root'
})
export class LoginContentAdapterSupportService {
  helper = inject(LoginModalHelperService);
  pingAdapter = inject(PingLoginAdapterService);
  cookieHelper = inject(CookieHelperService);
  coreUtilService = inject(CoreUtilService);
  actiavtedRoute = inject(ActivatedRoute);
  utilHelper = inject(LoginHelperUtils);
  nextDisplayViewService = inject(NextDisplayViewService);

  setRememberMeValue(
    userInputFields: LoginAuthUserInputFields | undefined
  ): string {
    const promptCookie = this.cookieHelper.getCookieValue('prompt');
    const promptCookieAvailable = promptCookie ? true : false;
    const encryptedTokenStored = this.coreUtilService.get('encyToken');
    const uniqueIdentifierStored = this.coreUtilService.get('uid');
    if (
      this.setRememberMeValueCheck(
        promptCookieAvailable,
        userInputFields,
        encryptedTokenStored,
        uniqueIdentifierStored
      )
    ) {
      return 'Y';
    } else {
      return 'N';
    }
  }

  setRememberMeValueCheck(
    promptCookieAvailable: boolean,
    userInputFields: LoginAuthUserInputFields | undefined,
    encryptedTokenStored: string | null,
    uniqueIdentifierStored: string | null
  ) {
    return (
      !promptCookieAvailable &&
      !userInputFields &&
      (encryptedTokenStored || uniqueIdentifierStored)
    );
  }

  getFromApp(standalonePage: boolean): string {
    let fromApp = '';
    if (standalonePage) {
      fromApp = this.actiavtedRoute.snapshot.queryParamMap.get(
        FROM_APP
      ) as string;
    }
    return fromApp;
  }

  fromAppCheck(standalonePage: boolean, fromApp: string) {
    return (
      standalonePage &&
      this.actiavtedRoute.snapshot.queryParamMap.get(FROM_APP) &&
      !fromApp
    );
  }

  setPersistentLoginValue(
    userInputFields: LoginAuthUserInputFields | undefined
  ) {
    return userInputFields?.persistentLogin
      ? userInputFields?.persistentLogin
      : 'N';
  }

  async getLoginContent(
    loginAemResponse: LoginModalContent,
    config: OauthAEMConfig,
    skyMilesNumber: string
  ) {
    const allowAuthCallUrls =
      config.RESTRICT_AUTH_CALLS_URL ?? RESTRICT_AUTH_CALLS_URL;

    const screen = this.nextDisplayViewService.viewName$.getValue();

    return {
      formGroup: this.helper.createLoginForm(config),
      loginFormAttributes: this.helper.getLoginFormAttributes(),
      pageContent: this.helper.getLoginAuthenticationScreenContent(
        loginAemResponse,
        config
      ),
      authenticationEnabled: allowAuthCallUrls.some(
        (item) =>
          window.location.href.includes(item) ||
          screen === VIEW_NAME.LOGIN_CREDENTIALS_SCREEN
      )
        ? await this.pingAdapter.processPingAutenticationStarts(config)
        : '',
      skyMilesNumber: skyMilesNumber
    };
  }

  getRefreshUrlWithParamsPreserved(REFRESH_URL: string | null) {
    const url =
      REFRESH_URL && REFRESH_URL?.includes(window.location.origin)
        ? new URL(REFRESH_URL)
        : new URL(window.location.origin + REFRESH_URL);
    Object.entries(this.actiavtedRoute.snapshot.queryParams).forEach(
      ([key, value]) => {
        if (key !== 'refreshURL') {
          url.searchParams.append(key, value);
        }
      }
    );
    return decodeURIComponent(url.href);
  }
}
